import React from 'react';

import { defineMessage, IntlProvider, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { PopupTrigger } from '@atlassian/entry-points/popup-trigger';

import { loadingPopupEntryPoint } from '../fixtures/constellation/loading-popup-entry-point';
import { RelayWrapper } from '../utils/wrapper';

const openLabel = defineMessage({
	id: 'entry-points.examples.popup.open.ai-non-final',
	defaultMessage: 'Open popup',
	description: 'Label on the example button that opens a popup while its content loads.',
});
const closeLabel = defineMessage({
	id: 'entry-points.examples.popup.close.ai-non-final',
	defaultMessage: 'Close popup',
	description:
		'Label on the same trigger button while its popup is open; activating it dismisses the popup.',
});
const popupLabel = defineMessage({
	id: 'entry-points.examples.popup.label.ai-non-final',
	defaultMessage: 'Example popup',
	description: 'Accessible name of the example dialog popup, including while its content loads.',
});
const loadingMessage = defineMessage({
	id: 'entry-points.examples.popup.loading.ai-non-final',
	defaultMessage: 'Loading popup content',
	description: 'Message displayed while the example popup is waiting for its content to load.',
});
const loadedMessage = defineMessage({
	id: 'entry-points.examples.popup.loaded.ai-non-final',
	defaultMessage: 'Popup content loaded',
	description:
		'Completion announcement for popup content; this permanently loading example never reaches it.',
});
const errorTitle = defineMessage({
	id: 'entry-points.examples.popup.error-title.ai-non-final',
	defaultMessage: 'Could not load popup content',
	description: 'Heading shown when the popup content cannot load.',
});
const errorText = defineMessage({
	id: 'entry-points.examples.popup.error-text.ai-non-final',
	defaultMessage: 'Try again later.',
	description: 'Error message shown and announced when the popup content cannot load.',
});

const frenchMessages = {
	[openLabel.id]: 'Ouvrir la fenêtre',
	[closeLabel.id]: 'Fermer la fenêtre',
	[popupLabel.id]: 'Exemple de fenêtre',
	[loadingMessage.id]: 'Chargement du contenu',
	[loadedMessage.id]: 'Contenu chargé',
	[errorTitle.id]: 'Impossible de charger le contenu de la fenêtre',
	[errorText.id]: 'Réessayez plus tard.',
};

function TranslatedPopup(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<PopupTrigger
			entryPoint={loadingPopupEntryPoint}
			shouldRenderToParent
			role="dialog"
			label={formatMessage(popupLabel)}
			placement="bottom-start"
			loadingText={formatMessage(loadingMessage)}
			loadingCompleteText={formatMessage(loadedMessage)}
			errorTitle={formatMessage(errorTitle)}
			errorText={formatMessage(errorText)}
			trigger={(triggerProps) => (
				<Button {...triggerProps} appearance="primary" isSelected={triggerProps['aria-expanded']}>
					{formatMessage(triggerProps['aria-expanded'] ? closeLabel : openLabel)}
				</Button>
			)}
		/>
	);
}

export default function PopupTriggerInternationalization(): React.JSX.Element {
	return (
		<IntlProvider locale="fr" defaultLocale="en" messages={frenchMessages}>
			<RelayWrapper>
				<Box lang="fr">
					<TranslatedPopup />
				</Box>
			</RelayWrapper>
		</IntlProvider>
	);
}
